import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { AntiMetalButton } from '../components/ui/anti-metal-button';
import './GrowthServices.css'; /* reuse the same card CSS */

const PILLARS = [
  {
    id: 'marketing-intel',
    num: '01',
    label: 'Marketing Intelligence',
    headline: 'Understand what is driving acquisition, conversion and growth.',
    body: 'Connect your marketing data into one clear view so you always know what is working and where the money is going.',
    tags: [
      'Channel Performance',
      'Campaign Analytics',
      'Funnel Analysis',
      'Attribution',
      'Marketing Dashboards',
    ],
    accent: '#3b82f6',
  },
  {
    id: 'business-intel',
    num: '02',
    label: 'Business Intelligence',
    headline: 'Bring the important parts of your business into one view.',
    body: 'Stop switching between spreadsheets and tools. We build the single source of truth your leadership team actually uses.',
    tags: [
      'Business Dashboards',
      'KPI Reporting',
      'Data Visualisation',
      'Performance Monitoring',
      'Custom Reporting',
    ],
    accent: '#6366f1',
  },
  {
    id: 'data-analytics',
    num: '03',
    label: 'Data & Analytics',
    headline: 'Turn raw information into useful insight.',
    body: 'From customer behaviour to revenue trends — we find the signal in the noise and make it actionable.',
    tags: [
      'Data Analysis',
      'Data Integration',
      'Customer Insights',
      'Trend Analysis',
      'Decision Support',
    ],
    accent: '#22d3ee',
  },
  {
    id: 'custom-systems',
    num: '04',
    label: 'Custom Intelligence Systems',
    headline: 'Built around the way your business actually operates.',
    body: 'Because a generic dashboard is rarely the answer. We build reporting and intelligence systems around the metrics, teams and decisions that matter to you.',
    tags: [
      'Custom Dashboards',
      'Data Infrastructure',
      'Automated Reporting',
      'Multi-Source Integration',
      'Ongoing Optimisation',
    ],
    accent: '#a78bfa',
  },
];

function PillarCard({ pillar, index }) {
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`gs-card${visible ? ' gs-card--visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="gs-card-top">
        <span className="gs-card-num" style={{ color: pillar.accent }}>{pillar.num}</span>
        <span className="gs-card-label" style={{ color: pillar.accent, borderColor: `${pillar.accent}30`, background: `${pillar.accent}10` }}>
          {pillar.label}
        </span>
      </div>
      <div className="gs-card-rule" style={{ background: `linear-gradient(90deg, ${pillar.accent}, transparent)` }} />
      <h3 className="gs-card-headline">{pillar.headline}</h3>
      <p className="gs-card-body">{pillar.body}</p>
      <div className="gs-tags">
        {pillar.tags.map((tag) => (
          <span key={tag} className="gs-tag" style={{ borderColor: `${pillar.accent}25`, color: 'rgba(255,255,255,0.65)' }}>
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function IntelligenceServices() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('gs-section--visible'); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="gs-section" id="intelligence-services" ref={sectionRef}>
      <div className="gs-header">
        <p className="gs-eyebrow" style={{ color: '#3b82f6' }}>What We Do</p>
        <h2 className="gs-heading">
          Intelligence that covers<br />
          <span style={{
            background: 'linear-gradient(120deg, #fff 10%, rgba(59,130,246,0.9) 60%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            the full picture.
          </span>
        </h2>
        <p className="gs-subhead">
          Four interconnected disciplines — from marketing clarity to custom systems
          built around how your business actually makes decisions.
        </p>
      </div>

      <div className="gs-grid">
        {PILLARS.map((pillar, i) => (
          <PillarCard key={pillar.id} pillar={pillar} index={i} />
        ))}
      </div>

      <div className="gs-cta">
        <AntiMetalButton
          as="a"
          href="https://calendly.com/aurelixlabs/30min"
          target="_blank"
          rel="noopener noreferrer"
          accentFrom="#3b82f6"
          accentTo="#2563eb"
        >
          Explore Aurelix Intelligence
          <ArrowUpRight size={16} style={{ marginLeft: 4 }} />
        </AntiMetalButton>
      </div>
    </section>
  );
}
